// Custom localStorage cache for translations
const CACHE_PREFIX = 'translations_';
const CACHE_VERSION = 'v1.0.0';
const CACHE_EXPIRY_DAYS = 7;

class TranslationCache {
  constructor() {
    this.isAvailable = this.checkLocalStorageAvailability();
  }

  checkLocalStorageAvailability() {
    try {
      const test = 'localStorage-test';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      console.warn(
        'localStorage not available, translations will not be cached'
      );
      return false;
    }
  }

  getCacheKey(language) {
    return `${CACHE_PREFIX}${language}_${CACHE_VERSION}`;
  }

  getTimestampKey(language) {
    return `${CACHE_PREFIX}${language}_timestamp`;
  }

  isExpired(timestamp) {
    if (!timestamp) return true;
    const now = Date.now();
    const expiryTime = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    return now - timestamp > expiryTime;
  }

  get(language) {
    if (!this.isAvailable) return null;

    try {
      const timestampKey = this.getTimestampKey(language);
      const timestamp = localStorage.getItem(timestampKey);

      if (this.isExpired(parseInt(timestamp))) {
        this.remove(language);
        return null;
      }

      const cacheKey = this.getCacheKey(language);
      const cached = localStorage.getItem(cacheKey);

      if (cached) {
        console.log(`Using cached translations for ${language}`);
        return JSON.parse(cached);
      }
    } catch (error) {
      console.warn(
        `Failed to read cached translations for ${language}:`,
        error
      );
    }

    return null;
  }

  set(language, translations) {
    if (!this.isAvailable) return;

    try {
      const cacheKey = this.getCacheKey(language);
      const timestampKey = this.getTimestampKey(language);

      localStorage.setItem(cacheKey, JSON.stringify(translations));
      localStorage.setItem(timestampKey, Date.now().toString());

      console.log(`Cached translations for ${language}`);
    } catch (error) {
      console.warn(`Failed to cache translations for ${language}:`, error);
      // If localStorage is full, try to clear old entries
      this.clearExpired();
    }
  }

  remove(language) {
    if (!this.isAvailable) return;

    try {
      const cacheKey = this.getCacheKey(language);
      const timestampKey = this.getTimestampKey(language);

      localStorage.removeItem(cacheKey);
      localStorage.removeItem(timestampKey);
    } catch (error) {
      console.warn(
        `Failed to remove cached translations for ${language}:`,
        error
      );
    }
  }

  clearExpired() {
    if (!this.isAvailable) return;

    try {
      const keysToRemove = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
          if (key.endsWith('_timestamp')) {
            const timestamp = localStorage.getItem(key);
            if (this.isExpired(parseInt(timestamp))) {
              const language = key
                .replace(CACHE_PREFIX, '')
                .replace('_timestamp', '');
              keysToRemove.push(language);
            }
          }
        }
      }

      keysToRemove.forEach(language => this.remove(language));

      if (keysToRemove.length > 0) {
        console.log(
          `Cleared ${keysToRemove.length} expired translation cache entries`
        );
      }
    } catch (error) {
      console.warn('Failed to clear expired cache entries:', error);
    }
  }

  clear() {
    if (!this.isAvailable) return;

    try {
      const keysToRemove = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log(
        `Cleared all translation cache entries (${keysToRemove.length} items)`
      );
    } catch (error) {
      console.warn('Failed to clear translation cache:', error);
    }
  }
}

// Export singleton instance
export default new TranslationCache();
